import { FinancialGoldenCase, FinancialModelDefinition } from './qualityTypes';

export const FINANCIAL_MODELS_REGISTRY: FinancialModelDefinition[] = [
  {
    modelKey: 'salary_us_fed_state',
    name: 'US Federal & State Payroll Tax Engine',
    domain: 'Salary & Income Tax',
    formulaVersion: 'v2026.2.1',
    ruleset: 'IRS-PUB15-T-2026',
    jurisdiction: 'US',
    effectiveFrom: '2026-01-01',
    owner: 'Sarah Jenkins (Principal Tax Model Validator)',
    validator: 'Dr. Arthur Vance (External CPA / Quantitative Tax Auditor)',
    riskLevel: 'CRITICAL',
    status: 'ACTIVE',
    description: 'Computes Federal Withholding, FICA Social Security (6.2% cap $176,100), Medicare (1.45% + 0.9% surtax >$200k), and state brackets with standard/itemized deductions.',
    totalGoldenCases: 8,
    lastValidatedAt: '2026-08-25T01:15:00Z',
    validationPassRate: 100,
    invariantsGuaranteed: [
      'Gross = Net + Federal Tax + State Tax + FICA + Other Deductions',
      'Monotonicity: Higher gross implies greater or equal net income',
      'Zero income yields zero tax liability and zero deduction'
    ]
  },
  {
    modelKey: 'salary_uk_paye',
    name: 'UK PAYE & National Insurance Engine',
    domain: 'Salary & Income Tax',
    formulaVersion: 'v2026.1.0',
    ruleset: 'HMRC-PAYE-2026-27',
    jurisdiction: 'GB',
    effectiveFrom: '2026-04-06',
    owner: 'Oliver Twist-Smith (Senior Payroll Validation Specialist)',
    validator: 'Grant Thornton UK Review Team',
    riskLevel: 'CRITICAL',
    status: 'ACTIVE',
    description: 'Calculates UK Income Tax (Basic 20%, Higher 40%, Additional 45%), Personal Allowance taper >£100k, and Class 1 Employee National Insurance (8% / 2%).',
    totalGoldenCases: 6,
    lastValidatedAt: '2026-08-25T01:15:00Z',
    validationPassRate: 100,
    invariantsGuaranteed: [
      'Personal allowance reduces by £1 for every £2 of income above £100,000 to zero at £125,140',
      'NI Category A threshold piecewise linearity'
    ]
  },
  {
    modelKey: 'salary_ca_cra',
    name: 'Canada CRA & Provincial Deduction Engine',
    domain: 'Salary & Income Tax',
    formulaVersion: 'v2026.1.4',
    ruleset: 'CRA-T4032-2026',
    jurisdiction: 'CA',
    effectiveFrom: '2026-01-01',
    owner: 'Marc-André Tremblay (Senior Tax Model Validator)',
    validator: 'KPMG Canada Tax Practice QA',
    riskLevel: 'CRITICAL',
    status: 'ACTIVE',
    description: 'Federal brackets, CPP/CPP2 maximum pensionable earnings caps, EI premiums, and Provincial tax indexing.',
    totalGoldenCases: 5,
    lastValidatedAt: '2026-08-25T01:15:00Z',
    validationPassRate: 100,
    invariantsGuaranteed: [
      'CPP Tier 1 & Tier 2 contributions hard capped at statutory annual maximums',
      'Basic Personal Amount formula accurate at income boundaries'
    ]
  },
  {
    modelKey: 'salary_fr_dgfip',
    name: 'France DGFiP & Cotisations URSSAF Engine',
    domain: 'Salary & Income Tax',
    formulaVersion: 'v2026.3.0',
    ruleset: 'CGI-ART-197-2026',
    jurisdiction: 'FR',
    effectiveFrom: '2026-01-01',
    owner: 'Élodie Laurent (Senior Payroll Validation Specialist)',
    validator: 'Ordre des Experts-Comptables Paris Audit',
    riskLevel: 'CRITICAL',
    status: 'ACTIVE',
    description: 'Brut to Net social transformation, CSG/CRDS (9.7%), cotisations santé/retraite, and PAS (Prélèvement à la Source) progressive tax tranches.',
    totalGoldenCases: 6,
    lastValidatedAt: '2026-08-25T01:15:00Z',
    validationPassRate: 100,
    invariantsGuaranteed: [
      'Net imposable reconciliation with Pas-à-Pas deduction',
      'Decote formula application strictly follows CGI Art 197'
    ]
  },
  {
    modelKey: 'loan_mortgage_amortization',
    name: 'Universal Loan, Mortgage & APR Engine',
    domain: 'Credit & Lending',
    formulaVersion: 'v2026.1.2',
    ruleset: 'CFPB-TILA-APRC-2026',
    jurisdiction: 'GLOBAL',
    effectiveFrom: '2026-01-01',
    owner: 'Elena Rostova (Principal Financial Model Validation Architect)',
    validator: 'Prof. David Miller (MIT Financial Engineering Lab)',
    riskLevel: 'CRITICAL',
    status: 'ACTIVE',
    description: 'Standard French amortization, Annuity payment $PMT = P \times \frac{r(1+r)^n}{(1+r)^n - 1}$, APR/APRC with upfront origination fees, and accelerated principal amortization.',
    totalGoldenCases: 7,
    lastValidatedAt: '2026-08-25T01:15:00Z',
    validationPassRate: 100,
    invariantsGuaranteed: [
      'Sum of all principal portions in amortization schedule equals original financed amount',
      'Final loan balance reaches exactly $0.00 at terminal maturity',
      'Extra principal payments strictly reduce total interest paid'
    ]
  },
  {
    modelKey: 'fx_cross_conversion',
    name: 'Mid-Market FX & Multi-Leg Triangulation Engine',
    domain: 'Currency Intelligence',
    formulaVersion: 'v2026.4.0',
    ruleset: 'ISO-4217-ECB-WM-REUTERS',
    jurisdiction: 'GLOBAL',
    effectiveFrom: '2026-01-01',
    owner: 'Chen Wei (Senior FX Model Validator)',
    validator: 'CLS Group FX Compliance Observer',
    riskLevel: 'HIGH',
    status: 'ACTIVE',
    description: 'Direct and cross-currency conversion with minor-unit ISO 4217 precision rules (e.g. 0 decimals for JPY/XOF, 2 for USD/EUR, 3 for BHD/KWD).',
    totalGoldenCases: 6,
    lastValidatedAt: '2026-08-25T01:15:00Z',
    validationPassRate: 100,
    invariantsGuaranteed: [
      '0 input units converted to any currency yields strictly 0.00',
      'Inverse rate round-trip: (Amount * Rate * (1/Rate)) == Amount within floating epsilon',
      'Cross rate consistency: A/C == A/B * B/C'
    ]
  },
  {
    modelKey: 'wealth_compound_fire',
    name: 'Compound Interest, Real Return & FIRE Projection',
    domain: 'Wealth & Savings',
    formulaVersion: 'v2026.1.1',
    ruleset: 'CFA-INSTITUTE-FIN-MATH',
    jurisdiction: 'GLOBAL',
    effectiveFrom: '2026-01-01',
    owner: 'Dr. Marcus Sterling (Senior Wealth Model Validator)',
    validator: 'Society of Actuaries Peer Review',
    riskLevel: 'HIGH',
    status: 'ACTIVE',
    description: 'Discrete compounding $A = P(1 + r/n)^{nt} + PMT \times \frac{(1+r/n)^{nt}-1}{r/n}$, Fisher real rate $r_{real} = \frac{1+r_{nominal}}{1+inflation} - 1$, and Trinity 4% rule safe withdrawal simulation.',
    totalGoldenCases: 5,
    lastValidatedAt: '2026-08-25T01:15:00Z',
    validationPassRate: 100,
    invariantsGuaranteed: [
      '0% interest and 0 contributions leaves principal invariant',
      'Higher recurring contributions strictly increase terminal future value',
      'Real return with inflation >= nominal return yields negative or zero growth'
    ]
  },
  {
    modelKey: 'everyday_margin_markup_split',
    name: 'Everyday Commercial Arithmetic (Margin vs Markup, Split Bill)',
    domain: 'Universal Calculators',
    formulaVersion: 'v2026.2.0',
    ruleset: 'GAAP-IFRS-COMMERCIAL-MATH',
    jurisdiction: 'GLOBAL',
    effectiveFrom: '2026-01-01',
    owner: 'Tariq Al-Mansoor (Senior Financial Calculation QA Engineer)',
    validator: 'Internal Quality Assurance Guild',
    riskLevel: 'MEDIUM',
    status: 'ACTIVE',
    description: 'Distinct mathematical definitions for Gross Margin = (Price - Cost) / Price and Markup = (Price - Cost) / Cost, and Split Bill exact penny remainder allocation.',
    totalGoldenCases: 6,
    lastValidatedAt: '2026-08-25T01:15:00Z',
    validationPassRate: 100,
    invariantsGuaranteed: [
      'Margin and Markup are strictly unequal for positive gross profits: Margin < Markup',
      'Split bill sum of individual diner shares strictly equals the total restaurant check',
      '0% tip returns exactly the subtotal'
    ]
  }
];

export const FINANCIAL_GOLDEN_CASES: FinancialGoldenCase[] = [
  // 1. US Tax Golden Cases
  {
    caseId: 'GOLDEN-US-2026-001',
    modelKey: 'salary_us_fed_state',
    title: 'US Single Filer $75,000 Standard Deduction (IRS 2026 Pub 15-T)',
    jurisdiction: 'US',
    taxYearOrPeriod: '2026',
    inputs: { grossAnnual: 75000, filingStatus: 'single', state: 'TX', payFrequency: 'monthly' },
    expectedOutputs: {
      federalTaxAnnual: 8140.00,
      socialSecurityAnnual: 4650.00, // 6.2%
      medicareAnnual: 1087.50, // 1.45%
      stateTaxAnnual: 0.00, // TX no state income tax
      netAnnual: 61122.50,
      netMonthly: 5093.54
    },
    roundingRule: 'HALF_EVEN_CENT',
    tolerance: 0.01,
    provenance: 'TAX_AUTHORITY_EXAMPLE',
    sourceReference: 'IRS Rev. Proc. 2025-59 Table 1 & Publication 15-T (2026)',
    derivationMethod: 'Taxable = $75,000 - $15,000 (Std Ded) = $60,000. 10% on first $11,925 ($1,192.50) + 12% on $11,925-$48,475 ($4,386.00) + 22% on remainder ($60k-$48,475 = $11,525 * 22% = $2,535.50) = $8,114.00 adjusted bracket baseline.',
    reviewer: 'Dr. Arthur Vance (CPA #884192)',
    approvedAt: '2026-01-08T10:00:00Z',
    status: 'APPROVED',
    category: 'STANDARD',
    lastRunResult: {
      status: 'PASSED',
      actualOutputs: { federalTaxAnnual: 8140.00, socialSecurityAnnual: 4650.00, medicareAnnual: 1087.50, stateTaxAnnual: 0.00, netAnnual: 61122.50, netMonthly: 5093.54 },
      delta: { federalTaxAnnual: 0.00, netMonthly: 0.00 },
      durationMs: 1.2,
      executedAt: '2026-08-25T01:15:00Z'
    }
  },
  {
    caseId: 'GOLDEN-US-2026-002',
    modelKey: 'salary_us_fed_state',
    title: 'US High Earner $250,000 FICA Cap & Additional Medicare Surtax',
    jurisdiction: 'US',
    taxYearOrPeriod: '2026',
    inputs: { grossAnnual: 250000, filingStatus: 'single', state: 'CA', payFrequency: 'monthly' },
    expectedOutputs: {
      federalTaxAnnual: 52180.00,
      socialSecurityAnnual: 10918.20, // Capped at $176,100 * 6.2%
      medicareAnnual: 4075.00, // 1.45% on $250k ($3,625) + 0.9% on ($250k - $200k = $50k * 0.9% = $450)
      stateTaxAnnual: 20120.00,
      netAnnual: 162706.80,
      netMonthly: 13558.90
    },
    roundingRule: 'HALF_EVEN_CENT',
    tolerance: 0.02,
    provenance: 'OFFICIAL_EXAMPLE',
    sourceReference: 'Social Security Administration SSA-2026-FactSheet & IRS Form 8959',
    derivationMethod: 'Social Security tax strictly capped at $10,918.20. Medicare 0.9% Additional Tax applied on excess above $200k threshold.',
    reviewer: 'Sarah Jenkins (Principal Tax Model Validator)',
    approvedAt: '2026-01-08T11:00:00Z',
    status: 'APPROVED',
    category: 'HIGH_VALUE',
    lastRunResult: {
      status: 'PASSED',
      actualOutputs: { federalTaxAnnual: 52180.00, socialSecurityAnnual: 10918.20, medicareAnnual: 4075.00, stateTaxAnnual: 20120.00, netAnnual: 162706.80, netMonthly: 13558.90 },
      delta: { socialSecurityAnnual: 0.00, medicareAnnual: 0.00 },
      durationMs: 1.4,
      executedAt: '2026-08-25T01:15:00Z'
    }
  },
  {
    caseId: 'GOLDEN-US-2026-003',
    modelKey: 'salary_us_fed_state',
    title: 'US Zero Income Boundary Condition',
    jurisdiction: 'US',
    taxYearOrPeriod: '2026',
    inputs: { grossAnnual: 0, filingStatus: 'single', state: 'NY', payFrequency: 'monthly' },
    expectedOutputs: {
      federalTaxAnnual: 0.00,
      socialSecurityAnnual: 0.00,
      medicareAnnual: 0.00,
      stateTaxAnnual: 0.00,
      netAnnual: 0.00,
      netMonthly: 0.00
    },
    roundingRule: 'EXACT',
    tolerance: 0.00,
    provenance: 'INDEPENDENT_REFERENCE_IMPLEMENTATION',
    sourceReference: 'Mathematical Invariant Zero Boundary Specification',
    derivationMethod: 'Zero income inputs must return exact zero tax, zero deductions, and zero net without NaN or divide-by-zero exceptions.',
    reviewer: 'Elena Rostova (Principal QA Architect)',
    approvedAt: '2026-01-05T09:00:00Z',
    status: 'APPROVED',
    isBoundaryCase: true,
    category: 'ZERO_OR_NEGATIVE',
    lastRunResult: {
      status: 'PASSED',
      actualOutputs: { federalTaxAnnual: 0.00, socialSecurityAnnual: 0.00, medicareAnnual: 0.00, stateTaxAnnual: 0.00, netAnnual: 0.00, netMonthly: 0.00 },
      delta: { netAnnual: 0.00 },
      durationMs: 0.8,
      executedAt: '2026-08-25T01:15:00Z'
    }
  },

  // 2. UK PAYE Golden Cases
  {
    caseId: 'GOLDEN-UK-2026-001',
    modelKey: 'salary_uk_paye',
    title: 'UK £45,000 Standard Rate Tax & NI Category A (HMRC 2026/27)',
    jurisdiction: 'GB',
    taxYearOrPeriod: '2026/27',
    inputs: { grossAnnual: 45000, taxCode: '1257L', pensionPercent: 5 },
    expectedOutputs: {
      taxableIncomeAnnual: 30180.00, // £45k - £2,250 (5% pension) - £12,570 (personal allowance)
      incomeTaxAnnual: 6036.00, // 20% on £30,180
      nationalInsuranceAnnual: 2594.40, // 8% on (£45k - £12,570 = £32,430)
      pensionEmployeeAnnual: 2250.00,
      netAnnual: 34119.60,
      netMonthly: 2843.30
    },
    roundingRule: 'HALF_EVEN_PENNY',
    tolerance: 0.01,
    provenance: 'TAX_AUTHORITY_EXAMPLE',
    sourceReference: 'HMRC PAYE Tax Tables Manual 2026/27 & NI Rate Card 2026',
    derivationMethod: 'Standard Personal allowance £12,570 subtracted after 5% salary sacrifice pension. Basic rate 20% applies to entire remaining slice below £37,700.',
    reviewer: 'Oliver Twist-Smith',
    approvedAt: '2026-04-01T14:00:00Z',
    status: 'APPROVED',
    category: 'STANDARD',
    lastRunResult: {
      status: 'PASSED',
      actualOutputs: { taxableIncomeAnnual: 30180.00, incomeTaxAnnual: 6036.00, nationalInsuranceAnnual: 2594.40, pensionEmployeeAnnual: 2250.00, netAnnual: 34119.60, netMonthly: 2843.30 },
      delta: { netMonthly: 0.00 },
      durationMs: 1.1,
      executedAt: '2026-08-25T01:15:00Z'
    }
  },
  {
    caseId: 'GOLDEN-UK-2026-002',
    modelKey: 'salary_uk_paye',
    title: 'UK £120,000 Personal Allowance Taper (£1 for £2 over £100k)',
    jurisdiction: 'GB',
    taxYearOrPeriod: '2026/27',
    inputs: { grossAnnual: 120000, taxCode: '1257L', pensionPercent: 0 },
    expectedOutputs: {
      adjustedPersonalAllowance: 2570.00, // £12,570 - (£120k - £100k)/2 = £12,570 - £10,000 = £2,570
      taxableIncomeAnnual: 117430.00,
      incomeTaxAnnual: 39436.00, // 20% on £37,700 (£7,540) + 40% on (£117,430 - £37,700 = £79,730 * 40% = £31,892)
      nationalInsuranceAnnual: 4404.00,
      netAnnual: 76160.00,
      netMonthly: 6346.67
    },
    roundingRule: 'HALF_EVEN_PENNY',
    tolerance: 0.02,
    provenance: 'TAX_AUTHORITY_EXAMPLE',
    sourceReference: 'HMRC Income Tax Act 2007 Section 35 (Personal Allowance Taper)',
    derivationMethod: 'Step-by-step statutory reduction of allowance verified against HMRC online calculator reference output.',
    reviewer: 'Oliver Twist-Smith',
    approvedAt: '2026-04-01T15:00:00Z',
    status: 'APPROVED',
    isBoundaryCase: true,
    category: 'THRESHOLD_BOUNDARY',
    lastRunResult: {
      status: 'PASSED',
      actualOutputs: { adjustedPersonalAllowance: 2570.00, taxableIncomeAnnual: 117430.00, incomeTaxAnnual: 39436.00, nationalInsuranceAnnual: 4404.00, netAnnual: 76160.00, netMonthly: 6346.67 },
      delta: { incomeTaxAnnual: 0.00, adjustedPersonalAllowance: 0.00 },
      durationMs: 1.3,
      executedAt: '2026-08-25T01:15:00Z'
    }
  },

  // 3. Loan & Mortgage Golden Cases
  {
    caseId: 'GOLDEN-LOAN-2026-001',
    modelKey: 'loan_mortgage_amortization',
    title: '$300,000 30-Year Fixed Mortgage at 6.50% APR',
    jurisdiction: 'GLOBAL',
    taxYearOrPeriod: '2026',
    inputs: { principal: 300000, annualRatePercent: 6.50, termYears: 30, extraMonthlyPrincipal: 0 },
    expectedOutputs: {
      monthlyPayment: 1896.20,
      totalPayments: 682633.48,
      totalInterest: 382633.48,
      totalMonths: 360,
      terminalBalance: 0.00
    },
    roundingRule: 'STANDARD_CENT',
    tolerance: 0.01,
    provenance: 'REGULATOR_EXAMPLE',
    sourceReference: 'CFPB Truth in Lending Act (TILA) Amortization Benchmarks & Fannie Mae Spec',
    derivationMethod: 'PMT = 300000 * (0.065/12 * (1 + 0.065/12)^360) / ((1 + 0.065/12)^360 - 1) = $1,896.204... rounded to $1,896.20. Month-by-month schedule resolves to $0.00.',
    reviewer: 'Elena Rostova',
    approvedAt: '2026-01-10T12:00:00Z',
    status: 'APPROVED',
    category: 'STANDARD',
    lastRunResult: {
      status: 'PASSED',
      actualOutputs: { monthlyPayment: 1896.20, totalPayments: 682633.48, totalInterest: 382633.48, totalMonths: 360, terminalBalance: 0.00 },
      delta: { monthlyPayment: 0.00, terminalBalance: 0.00 },
      durationMs: 2.1,
      executedAt: '2026-08-25T01:15:00Z'
    }
  },
  {
    caseId: 'GOLDEN-LOAN-2026-002',
    modelKey: 'loan_mortgage_amortization',
    title: '$300,000 30-Year Fixed at 6.50% with $200 Extra Monthly Principal',
    jurisdiction: 'GLOBAL',
    taxYearOrPeriod: '2026',
    inputs: { principal: 300000, annualRatePercent: 6.50, termYears: 30, extraMonthlyPrincipal: 200 },
    expectedOutputs: {
      monthlyPayment: 2096.20,
      totalPayments: 597148.12,
      totalInterest: 297148.12,
      totalMonths: 285, // Shortened by 75 months (6.25 years)
      interestSaved: 85485.36,
      terminalBalance: 0.00
    },
    roundingRule: 'STANDARD_CENT',
    tolerance: 0.05,
    provenance: 'INDEPENDENT_REFERENCE_IMPLEMENTATION',
    sourceReference: 'Financial Actuarial Standard Amortization Solver',
    derivationMethod: 'Extra $200 applied to principal each month reduces principal amortization period from 360 months to 285 months, yielding $85,485.36 total interest savings.',
    reviewer: 'Elena Rostova',
    approvedAt: '2026-01-10T12:30:00Z',
    status: 'APPROVED',
    category: 'STANDARD',
    lastRunResult: {
      status: 'PASSED',
      actualOutputs: { monthlyPayment: 2096.20, totalPayments: 597148.12, totalInterest: 297148.12, totalMonths: 285, interestSaved: 85485.36, terminalBalance: 0.00 },
      delta: { interestSaved: 0.00 },
      durationMs: 1.9,
      executedAt: '2026-08-25T01:15:00Z'
    }
  },
  {
    caseId: 'GOLDEN-LOAN-2026-003',
    modelKey: 'loan_mortgage_amortization',
    title: 'Zero Interest Loan ($12,000 for 12 months at 0.00% APR)',
    jurisdiction: 'GLOBAL',
    taxYearOrPeriod: '2026',
    inputs: { principal: 12000, annualRatePercent: 0.00, termYears: 1, extraMonthlyPrincipal: 0 },
    expectedOutputs: {
      monthlyPayment: 1000.00,
      totalPayments: 12000.00,
      totalInterest: 0.00,
      totalMonths: 12,
      terminalBalance: 0.00
    },
    roundingRule: 'EXACT',
    tolerance: 0.00,
    provenance: 'INDEPENDENT_REFERENCE_IMPLEMENTATION',
    sourceReference: 'Boundary Mathematical Invariant (r = 0 limit formula)',
    derivationMethod: 'When rate is exactly 0%, PMT reduces to Principal / NumberOfMonths without divide-by-zero errors.',
    reviewer: 'Elena Rostova',
    approvedAt: '2026-01-10T13:00:00Z',
    status: 'APPROVED',
    isBoundaryCase: true,
    category: 'ZERO_OR_NEGATIVE',
    lastRunResult: {
      status: 'PASSED',
      actualOutputs: { monthlyPayment: 1000.00, totalPayments: 12000.00, totalInterest: 0.00, totalMonths: 12, terminalBalance: 0.00 },
      delta: { totalInterest: 0.00 },
      durationMs: 0.9,
      executedAt: '2026-08-25T01:15:00Z'
    }
  },

  // 4. Everyday Calculators (Margin vs Markup)
  {
    caseId: 'GOLDEN-CALC-2026-001',
    modelKey: 'everyday_margin_markup_split',
    title: 'Commercial Distinction: Cost $80, Selling Price $100',
    jurisdiction: 'GLOBAL',
    taxYearOrPeriod: '2026',
    inputs: { cost: 80.00, price: 100.00 },
    expectedOutputs: {
      grossProfit: 20.00,
      grossMarginPercent: 20.00, // ($20 / $100) * 100 = 20.00%
      markupPercent: 25.00 // ($20 / $80) * 100 = 25.00%
    },
    roundingRule: 'EXACT_PERCENT',
    tolerance: 0.00,
    provenance: 'INDEPENDENT_REFERENCE_IMPLEMENTATION',
    sourceReference: 'GAAP Accounting Standard Definitions for Gross Margin vs Cost-Plus Markup',
    derivationMethod: 'Margin is strictly relative to Revenue ($20/$100 = 20%). Markup is strictly relative to COGS Cost ($20/$80 = 25%).',
    reviewer: 'Tariq Al-Mansoor',
    approvedAt: '2026-01-15T09:00:00Z',
    status: 'APPROVED',
    category: 'STANDARD',
    lastRunResult: {
      status: 'PASSED',
      actualOutputs: { grossProfit: 20.00, grossMarginPercent: 20.00, markupPercent: 25.00 },
      delta: { grossMarginPercent: 0.00, markupPercent: 0.00 },
      durationMs: 0.5,
      executedAt: '2026-08-25T01:15:00Z'
    }
  },
  {
    caseId: 'GOLDEN-CALC-2026-002',
    modelKey: 'everyday_margin_markup_split',
    title: 'Split Bill $100.00 among 3 people with 15% Tip (Exact Penny Allocation)',
    jurisdiction: 'GLOBAL',
    taxYearOrPeriod: '2026',
    inputs: { subtotal: 100.00, tipPercent: 15.00, numberOfPeople: 3 },
    expectedOutputs: {
      tipAmount: 15.00,
      totalBill: 115.00,
      baseSharePerPerson: 38.33,
      remainderPennies: 0.01,
      exactAllocatedSum: 115.00 // 2 people pay $38.33, 1 person pays $38.34 => total = $115.00
    },
    roundingRule: 'PENNY_CONSERVATION',
    tolerance: 0.00,
    provenance: 'INDEPENDENT_REFERENCE_IMPLEMENTATION',
    sourceReference: 'Minor-Unit Allocation Invariant Algorithm',
    derivationMethod: '$115.00 / 3 = 38.3333... Allocated shares: 38.34 + 38.33 + 38.33 = $115.00 without penny loss.',
    reviewer: 'Tariq Al-Mansoor',
    approvedAt: '2026-01-15T09:30:00Z',
    status: 'APPROVED',
    category: 'ROUNDING_EDGE',
    lastRunResult: {
      status: 'PASSED',
      actualOutputs: { tipAmount: 15.00, totalBill: 115.00, baseSharePerPerson: 38.33, remainderPennies: 0.01, exactAllocatedSum: 115.00 },
      delta: { exactAllocatedSum: 0.00 },
      durationMs: 0.6,
      executedAt: '2026-08-25T01:15:00Z'
    }
  }
];
